import React from 'react';
import { motion } from 'framer-motion';
import { Package, DollarSign, Clock, Star, Plus, TrendingUp, Eye, ShoppingBag, BarChart3, MessageSquare } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useVendor } from '../context/VendorContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function VendorDashboardScreen() {
  const {
    navigate
  } = useNavigation();
  const {
    store,
    stats,
    orders
  } = useVendor();
  const quickActions = [{
    icon: Plus,
    label: 'Add Product',
    screen: Screen.VENDOR_ADD_PRODUCT,
    color: 'bg-primary'
  }, {
    icon: Package,
    label: 'Orders',
    screen: Screen.VENDOR_ORDERS,
    color: 'bg-accent'
  }, {
    icon: BarChart3,
    label: 'Analytics',
    screen: Screen.VENDOR_ANALYTICS,
    color: 'bg-success'
  }, {
    icon: MessageSquare,
    label: 'Messages',
    screen: Screen.VENDOR_MESSAGES,
    color: 'bg-warning'
  }];
  const recentOrders = orders.slice(0, 3);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-1710">
      <Header title="Vendor Dashboard" showBack={false} data-id="element-1711" />

      <div className="p-4 space-y-4" data-id="element-1712">
        {/* Store Header */}
        <div className="bg-gradient-to-br from-primary to-primary-600 p-5 rounded-2xl text-white shadow-lg" data-id="element-1713">
          <div className="flex items-center justify-between mb-3" data-id="element-1714">
            <div data-id="element-1715">
              <h2 className="text-xl font-bold" data-id="element-1716">{store.name}</h2>
              <div className="flex items-center gap-1 mt-1" data-id="element-1717">
                <Star className="w-4 h-4 fill-accent-300 text-accent-300" data-id="element-1718" />
                <span className="text-sm font-medium" data-id="element-1719">{store.rating}</span>
                <span className="text-xs text-white/70 ml-1" data-id="element-1720">
                  ({store.totalReviews} reviews)
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center" data-id="element-1721">
              <ShoppingBag className="w-6 h-6" data-id="element-1722" />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3" data-id="element-1723">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white p-4 rounded-xl shadow-sm border border-gray-50" data-id="element-1724">
            <div className="flex items-center gap-3 mb-2" data-id="element-1725">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center" data-id="element-1726">
                <Package className="w-5 h-5 text-primary" data-id="element-1727" />
              </div>
              <div data-id="element-1728">
                <p className="text-2xl font-bold text-gray-900" data-id="element-1729">
                  {stats.ordersToday}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1730">Orders Today</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white p-4 rounded-xl shadow-sm border border-gray-50" data-id="element-1731">
            <div className="flex items-center gap-3 mb-2" data-id="element-1732">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center" data-id="element-1733">
                <DollarSign className="w-5 h-5 text-success" data-id="element-1734" />
              </div>
              <div data-id="element-1735">
                <p className="text-2xl font-bold text-gray-900" data-id="element-1736">
                  ₦{(stats.revenueToday / 1000).toFixed(0)}k
                </p>
                <p className="text-xs text-gray-500" data-id="element-1737">Revenue Today</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-white p-4 rounded-xl shadow-sm border border-gray-50" data-id="element-1738">
            <div className="flex items-center gap-3 mb-2" data-id="element-1739">
              <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center" data-id="element-1740">
                <Clock className="w-5 h-5 text-warning" data-id="element-1741" />
              </div>
              <div data-id="element-1742">
                <p className="text-2xl font-bold text-gray-900" data-id="element-1743">
                  {stats.pendingOrders}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1744">Pending Orders</p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="bg-white p-4 rounded-xl shadow-sm border border-gray-50" data-id="element-1745">
            <div className="flex items-center gap-3 mb-2" data-id="element-1746">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center" data-id="element-1747">
                <Star className="w-5 h-5 text-accent" data-id="element-1748" />
              </div>
              <div data-id="element-1749">
                <p className="text-2xl font-bold text-gray-900" data-id="element-1750">
                  {stats.rating}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1751">Store Rating</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-1752">
          <h3 className="font-bold text-gray-900 mb-3" data-id="element-1753">Quick Actions</h3>
          <div className="grid grid-cols-4 gap-3" data-id="element-1754">
            {quickActions.map((action, idx) => <motion.button key={action.label} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.5 + idx * 0.1
          }} onClick={() => navigate(action.screen)} className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 active:scale-95 transition-all cursor-pointer touch-manipulation" data-id="element-1755">
                <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center text-white shadow-sm`} data-id="element-1756">
                  <action.icon className="w-6 h-6" data-id="element-1757" />
                </div>
                <span className="text-xs font-medium text-gray-700 text-center leading-tight" data-id="element-1758">
                  {action.label}
                </span>
              </motion.button>)}
          </div>
        </div>

        {/* Performance Summary */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-1759">
          <div className="flex items-center justify-between mb-3" data-id="element-1760">
            <h3 className="font-bold text-gray-900" data-id="element-1761">Performance</h3>
            <button onClick={() => navigate(Screen.VENDOR_ANALYTICS)} className="text-sm font-medium text-primary flex items-center gap-1" data-id="element-1762">
              View All
              <Eye className="w-4 h-4" data-id="element-1763" />
            </button>
          </div>
          <div className="space-y-3" data-id="element-1764">
            <div className="flex items-center justify-between" data-id="element-1765">
              <span className="text-sm text-gray-600" data-id="element-1766">Conversion Rate</span>
              <span className="text-sm font-bold text-gray-900" data-id="element-1767">12.5%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-1768">
              <div className="bg-primary h-2 rounded-full" style={{
              width: '12.5%'
            }} data-id="element-1769"></div>
            </div>

            <div className="flex items-center justify-between mt-4" data-id="element-1770">
              <span className="text-sm text-gray-600" data-id="element-1771">
                Store Visits (7 days)
              </span>
              <span className="text-sm font-bold text-gray-900 flex items-center gap-1" data-id="element-1772">
                <TrendingUp className="w-4 h-4 text-success" data-id="element-1773" />
                1,234
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-1774">
              <div className="bg-success h-2 rounded-full" style={{
              width: '68%'
            }} data-id="element-1775"></div>
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-1776">
          <div className="flex items-center justify-between mb-3" data-id="element-1777">
            <h3 className="font-bold text-gray-900" data-id="element-1778">Recent Orders</h3>
            <button onClick={() => navigate(Screen.VENDOR_ORDERS)} className="text-sm font-medium text-primary" data-id="element-1779">
              View All
            </button>
          </div>
          <div className="space-y-3" data-id="element-1780">
            {recentOrders.map(order => <div key={order.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl" data-id="element-1781">
                <img src={order.product.image} alt={order.product.name} className="w-12 h-12 rounded-lg object-cover" data-id="element-1782" />
                <div className="flex-1 min-w-0" data-id="element-1783">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1784">
                    {order.product.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1785">{order.buyerName}</p>
                </div>
                <div className="text-right" data-id="element-1786">
                  <p className="text-sm font-bold text-primary" data-id="element-1787">
                    ₦{(order.total / 1000).toFixed(0)}k
                  </p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${order.orderStatus === 'New' ? 'bg-warning/10 text-warning' : order.orderStatus === 'Processing' ? 'bg-primary/10 text-primary' : 'bg-success/10 text-success'}`} data-id="element-1788">
                    {order.orderStatus}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </motion.div>;
}